import React, { useEffect, useMemo, useState } from 'react';
import PageServices from '../../services/PageServices';
import CalendarPicker from './CalendarPicker';
import stockList from '../../stock.json';
import './SrStock.css';

// Stocks at Support & Resistance (AOC "S/R Stocks" style). GET /srStocks?date= returns the index /
// stock option chains with a 100% Volume, OI or OI change strike whose reversal price (Probable
// Swing column) is within Admin → Index % / Stock % of spot: PE side → Support, CE side →
// Resistance, with every time it happened. "Historical" opens the Backtester at that moment,
// "Live" the live option chain.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LOOKBACK_DAYS = 90;
const TODAY_REFRESH_MS = 60 * 1000;

const istNow = () => new Date(Date.now() + 330 * 60 * 1000); // read with UTC getters
const fmtDate = (d) => `${String(d.getUTCDate()).padStart(2, '0')}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
const tradingDays = () => {
  const now = istNow();
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const days = [];
  for (let back = 0; back <= LOOKBACK_DAYS; back++) {
    const d = new Date(start - back * 86400000);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) days.push(fmtDate(d));
  }
  return days;
};

const ist = (epochSec) => new Date((epochSec + 330 * 60) * 1000);
const fmtClock = (epochSec) => {
  const d = ist(epochSec);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};
const fmtAgo = (epochSec) => {
  const sec = Date.now() / 1000 - epochSec;
  if (sec < 3600) return `${Math.max(1, Math.floor(sec / 60))}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};
const money = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signed = (v) => `${v > 0 ? '+' : ''}${money(v)}`;

const openSymbol = (symbol, url) => {
  const lot = stockList.find((s) => s.name === symbol)?.lotSize;
  localStorage.setItem('stock', symbol);
  if (lot) localStorage.setItem('expiry', lot);
  window.open(url, '_blank', 'noopener,noreferrer');
};
const historicalUrl = (symbol, date, epochSec) => {
  const d = ist(epochSec);
  const time = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}:59`;
  return `/backtester?date=${encodeURIComponent(date)}&time=${encodeURIComponent(time)}&symbolToken=${encodeURIComponent(symbol)}`;
};

const SIDES = {
  support: { title: 'Support', letter: 'S' },
  resistance: { title: 'Resistance', letter: 'R' },
};

const Trend = ({ up }) => (
  <svg className="srs-trend" viewBox="0 0 16 16" aria-hidden="true">
    <path d={up ? 'M1 12l4.5-4.5 3 3L15 4M11 4h4v4' : 'M1 4l4.5 4.5 3-3L15 12M11 12h4V8'} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StockRow = ({ item, side, date }) => {
  const [open, setOpen] = useState(false);
  const latest = item.hits[0];
  const up = item.change >= 0;
  const letter = SIDES[side].letter;
  return (
    <div className={`srs-row srs-${side}`}>
      <div className="srs-row-main">
        <div className="srs-info">
          <div className="srs-name">
            <strong>{item.symbol}</strong>
            {item.count > 1 && (
              <button type="button" className="srs-count" onClick={() => setOpen((v) => !v)} aria-expanded={open} title={open ? 'Hide times' : 'Show all times'}>
                ×{item.count} <span className={open ? 'srs-caret open' : 'srs-caret'}>⌄</span>
              </button>
            )}
          </div>
          <small>{fmtClock(latest.time)} · {fmtAgo(latest.time)} · {letter} {money(latest.level)}</small>
        </div>
        <div className={`srs-price ${up ? 'srs-up' : 'srs-down'}`}>
          <strong><Trend up={up} /> {money(item.price)}</strong>
          <small>{signed(item.change)} ({signed(item.changePct)}%)</small>
        </div>
        <div className="srs-actions">
          <button type="button" className="srs-btn" onClick={() => openSymbol(item.symbol, historicalUrl(item.symbol, date, latest.time))}>Historical</button>
          <button type="button" className="srs-btn srs-btn-live" onClick={() => openSymbol(item.symbol, '/optionchain')}>Live</button>
        </div>
      </div>
      {open && (
        <ul className="srs-hits">
          {item.hits.map((hit) => (
            <li key={hit.time}>
              <span>
                {fmtClock(hit.time)} · strike {hit.strike} · {letter} {money(hit.level)} · spot {money(hit.spot)} · {hit.marks.join(' / ')} 100%
              </span>
              <button type="button" className="srs-link" onClick={() => openSymbol(item.symbol, historicalUrl(item.symbol, date, hit.time))}>Historical</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Panel = ({ side, items, date, status }) => (
  <section className={`srs-panel srs-panel-${side}`}>
    <header className="srs-panel-head">
      <span><i className="srs-dot" />{SIDES[side].title}</span>
      <span className="srs-total">{items.length}</span>
    </header>
    <div className="srs-list">
      {items.map((item) => <StockRow key={item.symbol} item={item} side={side} date={date} />)}
      {!items.length && status === 'ready' && <p className="srs-empty">No {SIDES[side].title.toLowerCase()} symbols</p>}
    </div>
  </section>
);

function SrStock() {
  const days = useMemo(tradingDays, []);
  const [date, setDate] = useState(days[0]);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    const load = (first) => {
      if (first) {
        setStatus('loading');
        setResult(null);
      }
      PageServices.getSrStocks(date)
        .then((res) => {
          if (cancelled) return;
          setResult(res?.data || null);
          setStatus('ready');
        })
        .catch(() => { if (!cancelled && first) setStatus('error'); });
    };
    load(true);
    const id = date === days[0] ? setInterval(() => load(false), TODAY_REFRESH_MS) : null; // today keeps updating
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [date, days]);

  const filter = (list) => {
    const q = search.trim().toUpperCase();
    return (list || []).filter((s) => !q || s.symbol.includes(q));
  };

  return (
    <section className="srs-page">
      <div className="srs-toolbar">
        <label className="srs-search">
          <i className="fa fa-search" aria-hidden="true" />
          <input type="search" placeholder="Search symbol" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="srs-date">
          <CalendarPicker
            value={date}
            dates={days}
            onChange={setDate}
            placeholder="Select Date"
            dialogLabel="Choose date"
            emptyText="No dates"
            align="right"
          />
        </div>
      </div>

      {status === 'loading' && <p className="srs-note">Scanning option chains for {date}…</p>}
      {status === 'error' && <p className="srs-note srs-down">Could not load S/R stocks. Please try again.</p>}
      {status === 'ready' && result && !result.configured && (
        <p className="srs-note">Index % and Stock % are not set yet (Admin → Index &amp; Stock %).</p>
      )}

      <div className="srs-grid">
        <Panel side="support" items={filter(result?.support)} date={date} status={status} />
        <Panel side="resistance" items={filter(result?.resistance)} date={date} status={status} />
      </div>
    </section>
  );
}

export default SrStock;
